import {
    ENTWURF_BLEIBT_MARKIERT, legeUnterlageAb, type AblageErgebnis, type AusgabeAnker,
} from '../../services/unterlagenAblage';
import {
    ablageFehler, ablageStandAus, pruefeAblageGroesse, type AblageStand,
} from '../../services/ablageHilfen';
import type { ReportMetadata } from '../../types';
import type { SigePlanAblageAnlass, SigePlanAblageAuftrag } from './planAblage';

/**
 * Der eine Weg des SiGe-Plans in die zentrale Ablage.
 *
 * Getrennt von `planAblage.ts`, damit die reinen Regeln ohne Supabase
 * testbar bleiben. Wirft nie: die Vorschau und der Export stehen nicht in
 * einem `try`, und ein gescheiterter Upload darf weder den Download noch die
 * Leistungsbuchung zurueckdrehen — abgelegt ist eine Zweitschrift, kein
 * Beleg der Buchung.
 *
 * Der Auftrag kommt FERTIG herein (`baueSigePlanAblageAuftrag`) und wird hier
 * nicht veraendert: so kann der Aufrufer ihn festhalten und bei „Erneut
 * ablegen" genau denselben noch einmal schicken — dieselben Bytes, dieselbe
 * Kennung — statt aus einem inzwischen geaenderten Plan ein neues Blatt unter
 * der alten Kennung abzulegen.
 *
 * Die Groesse wird VOR dem Upload geprueft. Grosse Plaene sind schon
 * komprimiert (`compress: true` im Generator); was dann noch ueber 50 MB
 * liegt, hilft nur kleinere Schrift oder weniger Piktogramme.
 *
 * ═══ AUSGABE FUER DEN AENDERUNGSDIENST (Stufe 6.1) ════════════════════════
 * Bei Freigabe und Versand bekommt der Kern zusaetzlich `ausgabe` und legt
 * zur Fassung einen Journal-Eintrag mit Steckbrief-Auszug an — den Anker,
 * gegen den spaetere Aenderungen der Planungsphase verglichen werden. Ein
 * Entwurf bekommt keinen: er ist kein Stand, nach dem gearbeitet wird. Die
 * Stammdaten (`metadata`) kommen vom Editor und stehen NICHT im Auftrag.
 *
 * ═══ „ERNEUT ABLEGEN" MIT DEM ANKER DES ERSTEN VERSUCHS ═══════════════════
 * Der Kern bildet den Steckbrief-Auszug VOR dem Hochladen und gibt ihn
 * zurueck. `wiederholung` ist der Auftrag fuer den naechsten Versuch: derselbe
 * Auftrag, dazu dieser Anker (`anker`). Der Editor haelt ihn statt des
 * Originals fest — so friert ein spaeterer Klick den Stand der Ausgabe ein,
 * nicht den inzwischen geaenderten (Codex-Review 17.10.2026).
 */

/** Was der Kern fuer den Journal-Eintrag des Plans braucht. */
export interface SigePlanAusgabe {
    unterlage: 'sigeplan';
    metadata: Partial<ReportMetadata> | null;
    /** Nur bei der Wiederholung gesetzt — der Anker des ersten Versuchs (oder `null`). */
    anker?: AusgabeAnker | null;
}

/**
 * Nur Freigabe und Versand sind Ausgaben; der Entwurf bekommt `undefined`.
 * Ein uebernommener Anker (auch `null`) geht mit; beim ersten Versuch fehlt
 * das Feld, und der Kern bildet ihn selbst.
 */
export const sigePlanAusgabe = (
    anlass: SigePlanAblageAnlass,
    metadata?: Partial<ReportMetadata> | null,
    anker?: AusgabeAnker | null,
): SigePlanAusgabe | undefined => {
    if (anlass !== 'freigabe' && anlass !== 'versand') return undefined;
    const ausgabe: SigePlanAusgabe = { unterlage: 'sigeplan', metadata: metadata ?? null };
    if (anker !== undefined) ausgabe.anker = anker;
    return ausgabe;
};

/**
 * Der Auftrag fuer den naechsten Versuch. Beim ersten Versuch kommt der
 * Anker aus dem Ergebnis des Kerns (oder `null`, wenn er keinen bilden
 * konnte); ein schon uebernommener bleibt. Ein Entwurf hat keinen Anker und
 * wird unveraendert wiederholt.
 */
export const wiederholungAus = (
    auftrag: SigePlanAblageAuftrag,
    ergebnis: Pick<AblageErgebnis, 'anker'> | null,
): SigePlanAblageAuftrag => {
    if (auftrag.anlass === 'entwurf' || auftrag.anker !== undefined) return auftrag;
    return { ...auftrag, anker: ergebnis?.anker ?? null };
};

/**
 * Die Hinweise des Kerns zu einer Ablage — etwa, dass der Journal-Eintrag
 * ohne Planungsphase entstand. Kein Fehler: die Fassung liegt. Leere und
 * doppelte Saetze fallen weg.
 */
export const ablageHinweise = (ergebnis: { hinweise?: readonly string[] | null } | null | undefined): string[] =>
    Array.from(new Set((ergebnis?.hinweise || []).map(h => (h || '').trim()).filter(Boolean)));

/**
 * Der Hinweis „Entwurfszeile nicht umbeschriftet" (`ENTWURF_BLEIBT_MARKIERT`)
 * ist keine Randnotiz: die versendete Fassung steht in der Ablage noch als
 * ENTWURF. Er wandert deshalb aus den dezenten Hinweisen in die Warnung des
 * Standes — Banner und Vorschau zeigen ihn im Warnton statt „liegt bereits in
 * Dokumenten" im Erfolgston. Alle anderen Hinweise bleiben, wo sie sind.
 */
export const mitEntwurfsWarnung = (
    stand: AblageStand,
    hinweise: readonly string[],
): { stand: AblageStand; hinweise: string[] } => {
    const entwurf = hinweise.filter(h => h.startsWith(ENTWURF_BLEIBT_MARKIERT));
    if (stand.art !== 'abgelegt' || !entwurf.length) return { stand, hinweise: [...hinweise] };
    return {
        stand: { ...stand, warnung: [stand.warnung, ...entwurf].filter(Boolean).join(' ') },
        hinweise: hinweise.filter(h => !h.startsWith(ENTWURF_BLEIBT_MARKIERT)),
    };
};

/** Stand fuer die Anzeige plus die dezenten Hinweise des Kerns. */
export interface SigePlanAblageErgebnis {
    stand: AblageStand;
    hinweise: string[];
    /** Der Auftrag fuer „Erneut ablegen" — derselbe, mit dem Anker dieses Vorgangs. */
    wiederholung: SigePlanAblageAuftrag;
    /**
     * Die Ausgabe (`projekt_unterlagen_ausgaben`), zu der diese Fassung gehoert
     * — durchgereicht aus `AblageErgebnis.ausgabeId`. Der Abschlussschritt
     * fragt mit ihr nach, welchen Bestandteil sie erledigt hat (Plan B5).
     * Fehlt beim Entwurf und wenn dieser Vorgang keine Ausgabe schrieb.
     */
    ausgabeId?: string;
    /** Eine Ausgabe, die zur Fassung schon frueher lag — nur zur Anzeige (`AblageErgebnis.vorhandeneAusgabeId`). */
    vorhandeneAusgabeId?: string;
}

export const legeSigePlanAb = async (
    auftrag: SigePlanAblageAuftrag,
    metadata?: Partial<ReportMetadata> | null,
): Promise<SigePlanAblageErgebnis> => {
    const pruefung = pruefeAblageGroesse(auftrag.datei?.size ?? 0);
    // Nichts gesendet, nichts gebildet: der Auftrag bleibt, wie er ist.
    if (pruefung.status === 'fehler') return { stand: ablageFehler(pruefung.grund), hinweise: [], wiederholung: auftrag };

    // `anker` ist eine Angabe an den Kern, kein Feld der Ablagezeile.
    const { anker, ...kernAuftrag } = auftrag;
    try {
        const ergebnis = await legeUnterlageAb({ ...kernAuftrag, ausgabe: sigePlanAusgabe(auftrag.anlass, metadata, anker) });
        return {
            ...mitEntwurfsWarnung(ablageStandAus(ergebnis, auftrag.titel), ablageHinweise(ergebnis)),
            wiederholung: wiederholungAus(auftrag, ergebnis),
            ...(ergebnis.ausgabeId ? { ausgabeId: ergebnis.ausgabeId } : {}),
            ...(ergebnis.vorhandeneAusgabeId ? { vorhandeneAusgabeId: ergebnis.vorhandeneAusgabeId } : {}),
        };
    } catch (e) {
        // `legeUnterlageAb` verspricht, nie zu werfen. Faellt das Versprechen
        // einmal, bleibt es trotzdem bei einer Meldung statt eines weissen
        // Fensters — und die Wiederholung traegt keinen heutigen Stand nach.
        return { stand: ablageFehler(e instanceof Error ? e.message : String(e)), hinweise: [], wiederholung: wiederholungAus(auftrag, null) };
    }
};
